import { ComponentProps, FC, ReactElement } from 'react';
import {
  timeScalarToPlainString,
  type TimeScalar,
} from '@bts-web/utils-formatting';
import { Locale } from '@bts-web/utils-lokalise';
import { AcceptsDataTestid } from '@bts-web/utils-test/types';
import { css } from '@bts-web/utils-style-engine';
import { CopyToClipboardButton, Status } from '../../common';
import { RenderTransactionTime } from './RenderTransactionTime.use-client';
import { CryptoTransferTransactionStatus } from '../../crypto-transfer-details';
import {
  InternalSavingsPlanTransactionState,
  StakingTransactionStatus,
} from '@bts-web/data-layer/server';
import { AllTypeTransactionStatus, STATUS_COLORS } from '../types';
import { CryptoTransactionInfoStatus } from '../../crypto-transfer-details/utils/getCryptoTransferCryptoTransferDetailsLabelsByStatus';
import { TransactionTypes } from '../../transactions/types/transactions';

type BaseTransactionDetailsFieldProps = AcceptsDataTestid & {
  label: string;
  endSlot?: ReactElement;
  locale: string;
  disabled?: boolean;
};

export enum TransactionDetailsFieldVariants {
  TRANSACTION = 'transaction',
  SAVINGS_PLAN = 'savings-plan',
  WALLET_ADDRESS = 'wallet-address',
  TRANSACTION_DATE = 'transaction-date',
  TRANSACTION_DATE_HOUR = 'transaction-date-hour',
  TRANSACTION_STATUS = 'transaction-status',
  CRYPTO_TRANSACTION_STATUS = 'crypto-transaction-status',
}

type TransactionDetailsFieldBody =
  | {
      variant: TransactionDetailsFieldVariants.TRANSACTION_DATE;
      value: TimeScalar;
      status?: never;
      transactionType?: never;
    }
  | {
      variant: TransactionDetailsFieldVariants.TRANSACTION_DATE_HOUR;
      value: TimeScalar;
      status?: never;
      transactionType?: never;
    }
  | {
      variant?:
        | TransactionDetailsFieldVariants.TRANSACTION
        | TransactionDetailsFieldVariants.SAVINGS_PLAN
        | TransactionDetailsFieldVariants.WALLET_ADDRESS;
      value: string;
      status?: never;
      transactionType?: never;
    }
  | {
      variant:
        | TransactionDetailsFieldVariants.TRANSACTION_STATUS
        | TransactionDetailsFieldVariants.CRYPTO_TRANSACTION_STATUS;
      value: string;
      status:
        | AllTypeTransactionStatus
        | InternalSavingsPlanTransactionState
        | StakingTransactionStatus
        | null
        | undefined;
      transactionType: TransactionTypes | undefined;
    };

type TransactionDetailsFieldProps = BaseTransactionDetailsFieldProps &
  TransactionDetailsFieldBody;

type TransactionValueStringProps = Extract<
  TransactionDetailsFieldBody,
  { value: string }
>;

const TransactionValueString: FC<TransactionValueStringProps> = ({
  value,
  variant,
  status,
  transactionType,
}) => {
  const statusVisualVariant =
    status && status in STATUS_COLORS
      ? (STATUS_COLORS[status as AllTypeTransactionStatus] as ComponentProps<
          typeof Status
        >['visual'])
      : undefined;

  const variantComponents = {
    [TransactionDetailsFieldVariants.CRYPTO_TRANSACTION_STATUS]: () => (
      <CryptoTransferTransactionStatus
        text={value}
        status={status as CryptoTransactionInfoStatus}
        transactionType={transactionType}
      />
    ),
    [TransactionDetailsFieldVariants.TRANSACTION_STATUS]: () => (
      <Status text={value} size="medium" visual={statusVisualVariant} />
    ),
    [TransactionDetailsFieldVariants.WALLET_ADDRESS]: () => (
      <div
        className={css({
          flex: '1',
          minWidth: '0px',
        })}
      >
        <CopyToClipboardButton
          value={value}
          visual="ghost"
          noBorder
          fullWidth
          ariaLabel={`${TransactionDetailsFieldVariants.WALLET_ADDRESS} copy`}
        >
          <span
            className={css({
              flex: 1,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textAlign: 'end',
              marginInlineEnd: 'auto',
            })}
          >
            {value}
          </span>
        </CopyToClipboardButton>
      </div>
    ),
    default: () => value,
  };

  const render =
    variantComponents[variant as keyof typeof variantComponents] ||
    variantComponents.default;

  return <>{render()}</>;
};

const TransactionValue: FC<{
  field: TransactionDetailsFieldBody;
  locale: string;
}> = ({ field, locale }) => {
  if (field.variant === TransactionDetailsFieldVariants.TRANSACTION_DATE) {
    return (
      <RenderTransactionTime
        time={field.value}
        locale={locale as Locale}
        mode="date-only"
      />
    );
  }

  if (field.variant === TransactionDetailsFieldVariants.TRANSACTION_DATE_HOUR) {
    return (
      <RenderTransactionTime
        time={field.value}
        locale={locale as Locale}
        mode="date-hour"
      />
    );
  }

  return <TransactionValueString {...field} />;
};

export const TransactionDetailsField: FC<TransactionDetailsFieldProps> = (
  props,
) => {
  const { label, endSlot, locale, disabled, 'data-testid': dataTestId } = props;

  const variant = props.variant ?? TransactionDetailsFieldVariants.TRANSACTION;

  const ariaValue =
    typeof props.value === 'string'
      ? props.value
      : timeScalarToPlainString(props.value);

  return (
    <li
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'small',
        flexWrap: 'wrap',
        maxWidth: '100%',
        ...([
          TransactionDetailsFieldVariants.TRANSACTION_DATE,
          TransactionDetailsFieldVariants.TRANSACTION_DATE_HOUR,
          TransactionDetailsFieldVariants.TRANSACTION,
          TransactionDetailsFieldVariants.TRANSACTION_STATUS,
          TransactionDetailsFieldVariants.CRYPTO_TRANSACTION_STATUS,
          TransactionDetailsFieldVariants.WALLET_ADDRESS,
        ].includes(variant)
          ? { borderBottomWidth: '1px', borderBottomColor: 'neutrals.divider' }
          : {}),
        py: 'medium',
      })}
      data-testid={dataTestId}
      aria-label={`${label}-${ariaValue}`}
    >
      <div
        className={css({
          color: disabled ? 'negative.text_primary' : 'neutrals.text_primary',
          fontSize: 'label.medium_medium',
          fontWeight: 'label.medium_medium',
          letterSpacing: 'label.medium_medium',
          lineHeight: 'label.medium_medium',
          flex: 1,
        })}
        role="presentation"
      >
        {label}
      </div>
      <div
        className={css({
          textAlign: 'end',
          minWidth: 0,
          textOverflow: 'ellipsis',
          fontSize: 'caption.medium_medium',
          fontWeight: 'caption.medium_medium',
          lineHeight: 'caption.medium_medium',
          letterSpacing: 'caption.medium_medium',
          marginInlineEnd: 'auto',
          ...(disabled ? { color: 'negative.text_primary' } : {}),
        })}
      >
        <TransactionValue field={props} locale={locale} />
      </div>
      {endSlot}
    </li>
  );
};
